import {useCallback, useEffect, useRef, useState} from 'react';
import {
  cardTokenizationPreparationErrorMessage, isCardTokenizationPreparationError, prepareCardTokenization,
  retryCardTokenizationPreparation, tokenizeCard as requestCardToken
} from '../services/cardTokenization';

export type CardTokenizationStatus = 'idle' | 'loading' | 'ready' | 'error';

export const useCardTokenization = (active: boolean) => {
  const [status, setStatus] = useState<CardTokenizationStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const activeRef = useRef(active);
  const requestVersionRef = useRef(0);
  activeRef.current = active;

  const prepare = useCallback(async (retry: boolean) => {
    const requestVersion = ++requestVersionRef.current;
    setStatus('loading');
    setErrorMessage('');

    try {
      if (retry) await retryCardTokenizationPreparation();
      else await prepareCardTokenization();

      if (activeRef.current && requestVersion === requestVersionRef.current) setStatus('ready');

    } catch {
      if (activeRef.current && requestVersion === requestVersionRef.current) {
        setStatus('error');
        setErrorMessage(cardTokenizationPreparationErrorMessage);
      }
    }
  }, []);

  useEffect(() => {
    if (!active) {
      requestVersionRef.current += 1;
      setStatus('idle');
      setErrorMessage('');
      return;
    }

    void prepare(false);
    return () => {
      requestVersionRef.current += 1;
    };
  }, [active, prepare]);

  const retry = useCallback(() => {
    if (activeRef.current) void prepare(true);
  }, [prepare]);

  const tokenizeCard = useCallback(async (number: string, code: string) => {
    try {
      return await requestCardToken(number, code);

    } catch (error) {
      if (activeRef.current && isCardTokenizationPreparationError(error)) {
        setStatus('error');
        setErrorMessage(cardTokenizationPreparationErrorMessage);
      }
      throw error;
    }
  }, []);

  return {status, errorMessage, retry, tokenizeCard};
};
